import * as Rx from 'rxjs';
import * as RxOp from 'rxjs/operators';
import { getPosApiUrl, getAccessToken } from '../../utils/state';
import { cancelOrderById } from '../../utils/api/client';
import {
  SET_VISIT_EXPIRES_AT,
  VISIT_EXPIRATION_WARNING,
  VISIT_EXPIRED,
  CLEAR_VISIT_EXPIRATION,
  VISIT_EXPIRATION_WARNING_THRESHOLD_SECONDS,
  setVisitExpiresAt,
  visitExpirationWarning,
  visitExpirationTick,
  visitExpired,
  clearVisitExpiration,
} from './VisitExpiration.state';
import {
  GET_ORDER_SUCCESS,
  clearCart,
  CLEAR_CART,
  HOLD_ORDER_SUCCESS,
  CANCEL_ORDER_SUCCESS,
  selectHasSuccessfulPayments,
} from './Cart.state';
import { POST_VISIT_SUCCESS } from '../checkout/Checkout.state';
import { replaceSharedAlerts } from '../../state/shared';
import { EMPTY_ACTION } from '../../state';
import { cartCancellationRequiresAssistance } from './orderCancellation';

const CANCELLATION_ACTIONS = [
  CLEAR_VISIT_EXPIRATION,
  HOLD_ORDER_SUCCESS,
  CANCEL_ORDER_SUCCESS,
  CLEAR_CART,
];

/**
 * When the cart receives order data containing visit_expires_at,
 * dispatch SET_VISIT_EXPIRES_AT to start tracking expiration.
 */
export const trackVisitExpiresAtEpic = (action$) =>
  action$.ofType(GET_ORDER_SUCCESS, POST_VISIT_SUCCESS).pipe(
    RxOp.map((action) => {
      const visitExpiresAt =
        action.payload?.visit_expires_at ||
        action.payload?.data?.visit_expires_at;
      if (visitExpiresAt) {
        return setVisitExpiresAt(visitExpiresAt);
      }
      return EMPTY_ACTION;
    }),
  );

/**
 * When visit_expires_at is set, compute the delay until the warning threshold
 * (VISIT_EXPIRATION_WARNING_THRESHOLD_SECONDS before expiration) and schedule the warning.
 * Cancels if visit expiration is cleared (order completed, cancelled, held, cart cleared).
 */
export const visitExpirationWarningTimerEpic = (action$) =>
  action$.ofType(SET_VISIT_EXPIRES_AT).pipe(
    RxOp.switchMap((action) => {
      const expiresAt = new Date(action.payload).getTime();
      const now = Date.now();
      const msUntilWarning =
        expiresAt - now - VISIT_EXPIRATION_WARNING_THRESHOLD_SECONDS * 1000;

      if (msUntilWarning <= 0) {
        // Already past warning threshold — show warning immediately with remaining time
        const secondsLeft = Math.max(0, Math.round((expiresAt - now) / 1000));
        if (secondsLeft <= 0) {
          return Rx.of(visitExpired());
        }
        return Rx.of(visitExpirationWarning(secondsLeft));
      }

      return Rx.timer(msUntilWarning).pipe(
        RxOp.map(() => {
          const remaining = Math.max(
            0,
            Math.round((expiresAt - Date.now()) / 1000),
          );
          return visitExpirationWarning(remaining);
        }),
        RxOp.takeUntil(action$.ofType(...CANCELLATION_ACTIONS)),
      );
    }),
  );

/**
 * Once the warning is shown, tick down every second until expiration.
 * Dispatches VISIT_EXPIRATION_TICK each second, then VISIT_EXPIRED when it reaches 0.
 */
export const visitExpirationCountdownEpic = (action$) =>
  action$.ofType(VISIT_EXPIRATION_WARNING).pipe(
    RxOp.switchMap((action) => {
      const startSeconds = action.payload;

      return Rx.timer(1000, 1000).pipe(
        RxOp.map((tick) => {
          const remaining = startSeconds - (tick + 1);
          if (remaining <= 0) {
            return visitExpired();
          }
          return visitExpirationTick(remaining);
        }),
        RxOp.takeWhile((action) => action.type !== VISIT_EXPIRED, true),
        RxOp.takeUntil(action$.ofType(...CANCELLATION_ACTIONS)),
      );
    }),
  );

/**
 * When the visit has expired:
 * - If order has successful payments (split-tender): do nothing automatic,
 *   show alert for manual intervention
 * - Otherwise: auto-cancel
 * Then clear the cart (if cancelled), and show a toast.
 */
export const visitExpiredActionEpic = (action$, state$) =>
  action$.ofType(VISIT_EXPIRED).pipe(
    RxOp.mergeMap(() => {
      const order = state$.value.cart?.order;
      const orderId = order?.order_id;

      if (!orderId) {
        return Rx.of(clearVisitExpiration());
      }

      const hasSuccessfulPayments = selectHasSuccessfulPayments(state$.value);

      if (hasSuccessfulPayments) {
        // Split-tender: require manual intervention
        return Rx.of(
          clearVisitExpiration(),
          replaceSharedAlerts({
            heading: 'Order Expired',
            text: `Order ${orderId} expired. Please manually void the payments and cancel the order.`,
            variant: 'error',
          }),
        );
      }

      // No successful payments: auto-cancel
      const apiParams = {
        url: getPosApiUrl(state$.value),
        token: getAccessToken(state$.value),
      };

      return Rx.defer(() =>
        cancelOrderById({
          ...apiParams,
          id: orderId,
          note: 'Order automatically cancelled: visit session expired',
        }),
      ).pipe(
        RxOp.mergeMap((cancelOrderResponse) =>
          Rx.of(
            clearVisitExpiration(),
            clearCart(),
            replaceSharedAlerts(
              cartCancellationRequiresAssistance(cancelOrderResponse)
                ? {
                    heading: 'Order Expired',
                    text: `Order ${orderId} expired, and its cancellation requires assistance. You may begin a new order and view this order in the order history page.`,
                    variant: 'error',
                  }
                : {
                    heading: 'Order Expired',
                    text: `Order ${orderId} expired. You can view the order in the order history page.`,
                    variant: 'info',
                  },
            ),
          ),
        ),
        RxOp.catchError(() => {
          return Rx.of(
            clearVisitExpiration(),
            replaceSharedAlerts({
              heading: 'Order Expiration Error',
              text: `Order ${orderId} expired but could not be cancelled automatically. Please cancel manually.`,
              variant: 'error',
            }),
          );
        }),
      );
    }),
  );

/**
 * Clear visit expiration tracking when the cart is cleared
 * or the order is held/cancelled.
 * PAYMENT_SUCCESS is intentionally excluded: for split-tender orders,
 * the countdown must continue after partial payments. Completed orders
 * dispatch CLEAR_CART which handles cleanup.
 */
export const clearVisitExpirationOnCartClearEpic = (action$) =>
  action$
    .ofType(HOLD_ORDER_SUCCESS, CANCEL_ORDER_SUCCESS, CLEAR_CART)
    .pipe(RxOp.map(() => clearVisitExpiration()));
